import React, { useEffect, useState, useRef } from 'react';
import { FaListAlt, FaTimes } from 'react-icons/fa';
import './ChatbotWidget.scss';

const ChatbotWidget = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const hostRef = useRef(null);
    const containerRef = useRef(null);
    const iframeRef = useRef(null);

    useEffect(() => {
        // 创建 shadow container
        const host = document.createElement('div');
        host.id = 'chatbot-launcher-shadow';
        hostRef.current = host;
        
        // 创建样式元素
        const style = document.createElement('style');
        style.textContent = `
            .chatbot-container {
                width: 100%;
                height: 100%;
                opacity: 0;
                transform: translateY(10px);
                transition: all 0.3s ease;
                border-radius: 0;
            }
            .chatbot-container.visible {
                opacity: 1;
                transform: translateY(0);
            }
            .loading-container {
                width: 100%;
                height: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                background-color: #ffffff;
                color: #666666;
                border-radius: 0;
            }
            iframe {
                width: 100%;
                height: 100%;
                border: none;
                border-radius: 0;
            }
        `;

        const shadow = host.attachShadow({ mode: 'open' });
        shadow.appendChild(style);

        // 创建容器
        const container = document.createElement('div');
        container.className = 'chatbot-container';
        containerRef.current = container;

        // 创建加载状态容器
        const loadingContainer = document.createElement('div');
        loadingContainer.className = 'loading-container';
        loadingContainer.textContent = 'Loading...';
        container.appendChild(loadingContainer);

        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.setAttribute('allow', 'cross-origin-isolated');
        iframe.setAttribute('crossorigin', 'anonymous');
        iframeRef.current = iframe;

        // 添加错误处理
        iframe.onerror = () => {
            console.error('Chatbot iframe failed to load');
            setHasError(true);
            setIsLoading(false);
            loadingContainer.textContent = 'Chatbot service is temporarily unavailable';
            loadingContainer.style.color = '#ff4444';
        };

        // 添加加载完成处理
        iframe.onload = () => {
            setIsLoading(false);
            iframe.style.display = 'block';
            loadingContainer.style.display = 'none';
        };

        // 设置 iframe src
        setIsLoading(true);
        setHasError(false);
        
        try {
            // 使用新的CloudFront URL
            const chatbotUrl = 'https://d9untg324dg08.cloudfront.net';
            iframe.src = chatbotUrl;
        } catch (error) {
            console.error('Error setting chatbot URL:', error);
            setHasError(true);
            setIsLoading(false);
            loadingContainer.textContent = 'Error loading chatbot';
            loadingContainer.style.color = '#ff4444';
        }

        container.appendChild(iframe);
        shadow.appendChild(container);
        document.body.appendChild(host);

        return () => {
            document.body.removeChild(host);
        };
    }, []);

    // 处理加载超时
    useEffect(() => {
        if (isLoading) {
            const timeoutId = setTimeout(() => {
                console.error('Chatbot iframe loading timeout');
                setHasError(true);
                setIsLoading(false);
                if (containerRef.current) {
                    const loadingContainer = containerRef.current.querySelector('.loading-container');
                    if (loadingContainer) {
                        loadingContainer.textContent = 'Chatbot service is temporarily unavailable';
                        loadingContainer.style.color = '#ff4444';
                    }
                }
            }, 10000);

            return () => clearTimeout(timeoutId);
        }
    }, [isLoading]);

    useEffect(() => {
        if (hostRef.current && containerRef.current) {
            if (isVisible) {
                hostRef.current.style.display = 'block';
                setTimeout(() => {
                    hostRef.current.classList.add('visible');
                    containerRef.current.classList.add('visible');
                }, 100);
            } else {
                hostRef.current.classList.remove('visible');
                containerRef.current.classList.remove('visible');
                setTimeout(() => {
                    hostRef.current.style.display = 'none';
                }, 300);
            }
        }
    }, [isVisible]);

    const toggleChatbot = () => {
        setIsVisible(!isVisible);
    };

    return (
        <button 
            className={`chatbot-toggle-button ${isVisible ? 'active' : ''}`}
            onClick={toggleChatbot}
            title={hasError ? "Chatbot service is temporarily unavailable" : "Open chatbot"}
        >
            {isVisible ? <FaTimes /> : <FaListAlt />}
        </button>
    );
};

export default ChatbotWidget; 